import { useParams } from "react-router"
import * as Icons from "@nova/component-icons"
import { MemberExtended } from "@nova/schemas"
import { FunctionComponent, useEffect, useMemo, useRef } from "react"
import { Card } from "features/pilot-tracking/MetricLayout"
import { useBatchPilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { PilotTrackingCardChart, GaugeChart } from "@nova/component-charts"
import { PodiumBadge } from "features/pilot-tracking/podium-badge/PodiumBadge"
import { MissionsTable } from "features/pilot-tracking/flights-page/MissionsTable"
import { PilotMapView } from "features/pilot-tracking/pilots/PilotMapView"
import { useAircraftImage } from "@nova/component-aircraft-images"
import { formatDistance } from "@nova/format"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { getGenericWorkspacePageWithoutHeader } from "../WorkspaceRoutes"
import { InputOutlined } from "@nova/ds-component-input"
import { useNonDefaultPilotTrackingNestedSidebar } from "panels/pilot-tracking/NestedSidebars"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActivityTimeRange,
    selectHistoryView,
    selectTab,
    setHistoryView,
    setTab,
} from "features/pilot-tracking/pilots/slice"
import { MissionsTableFilterBar } from "features/pilot-tracking/flights-page/MissionsTableFilterBar"
import { PilotHistoryTabSwitcher } from "features/pilot-tracking/pilots/PilotHistoryTabSwitcher"
import { selectFlightsSearch } from "features/pilot-tracking/redux/missions-table-selectors"
import { setSearch } from "features/pilot-tracking/redux/missions-table-slice"
import { PilotTrackingPilotNestedSidebar } from "features/pilot-tracking/pilots/PilotTrackingPilotNestedSidebar"
import { PilotActivityGraph } from "features/pilot-tracking/pilots/PilotActivityGraph"
import { padTimeSeriesData } from "features/pilot-tracking/pilots/pilotDataTransformation"
import { PilotCertificates } from "features/pilot-tracking/pilots/PilotCertificates"
import { getPilotOverviewQueries } from "features/pilot-tracking/pilots/PilotQueries"
import { createPortal } from "react-dom"
import { useIsVisible, useMergeRefs, useObserveElementRect } from "@nova/toolkit-pure-hooks"
import { getDisplayableAircraftData, zIndex } from "@nova/constants"
import {
    PilotPageHeader,
    PilotPageStickyHeader,
} from "features/pilot-tracking/pilots/PilotPageHeaders"
import { PlatformActivityCard } from "features/pilot-tracking/pilots/PlatformActivityCard"
import { CardCollection } from "features/pilot-tracking/CardCollection"
import { useRouter } from "pages"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"

export const PilotPage = getGenericWorkspacePageWithoutHeader({
    Content: PilotPageContent,
    maxWidth: 1264,
})

function PilotPageContent() {
    useNonDefaultPilotTrackingNestedSidebar({ Sidebar: PilotTrackingPilotNestedSidebar })

    const dispatch = useAppDispatch()
    const tab = useAppSelector(selectTab)
    const { pilotId = "" } = useParams()
    const user = useAppSelector(selectWorkspaceMembers).find((e) => e.userId === pilotId)
    const { ref: rectRef, width, left } = useObserveElementRect()
    const { containerRef: visibleContainerRef, isVisible: isMainHeaderVisible } = useIsVisible()
    const scrollRef = useRef<HTMLDivElement>(null)
    const mergedRef = useMergeRefs(rectRef, visibleContainerRef, scrollRef)

    useEffect(() => {
        dispatch(setTab("overview"))
        scrollRef.current?.scrollIntoView({ behavior: "instant", block: "start" })
    }, [pilotId, dispatch])

    if (!user) return null

    return (
        <div className="flex flex-col px-8 h-full w-full">
            <div ref={mergedRef}>
                <PilotPageHeader user={user} />
            </div>
            {createPortal(
                <div
                    className="absolute top-2.5 -translate-y-px -translate-x-px pointer-events-none"
                    style={{
                        left,
                        width,
                        zIndex: zIndex.HighPriorityModal,
                    }}
                >
                    <PilotPageStickyHeader user={user} visible={!isMainHeaderVisible} />
                </div>,
                document.getElementById("portal")!
            )}
            {tab === "overview" && <OverviewTab pilotId={pilotId} user={user} />}
            {tab === "history" && <HistoryTab userId={pilotId} />}
        </div>
    )
}

const OverviewTab = ({ pilotId, user }: { pilotId: string; user: MemberExtended }) => {
    const activityRange = useAppSelector(selectActivityTimeRange)
    const queries = useMemo(() => {
        const now = new Date()

        let currentRangeStart: Date | null = new Date(now.getTime())
        let previousRangeStart: Date | null = new Date(now.getTime())

        switch (activityRange) {
            case "all":
                currentRangeStart = null
                previousRangeStart = null
                break
            case "1y":
                currentRangeStart.setFullYear(currentRangeStart.getFullYear() - 1)
                previousRangeStart.setFullYear(previousRangeStart.getFullYear() - 2)
                break
            case "6m":
                currentRangeStart.setMonth(currentRangeStart.getMonth() - 6)
                previousRangeStart.setMonth(previousRangeStart.getMonth() - 12)
                break
            case "30d":
                currentRangeStart.setDate(currentRangeStart.getDate() - 29)
                previousRangeStart.setDate(previousRangeStart.getDate() - 58)
                break
            case "7d":
                currentRangeStart.setDate(currentRangeStart.getDate() - 6)
                previousRangeStart.setDate(previousRangeStart.getDate() - 12)
                break
        }

        return getPilotOverviewQueries({
            pilotId,
            currentRangeStart: currentRangeStart?.toISOString() ?? null,
            previousRangeStart: previousRangeStart?.toISOString() ?? null,
        })
    }, [activityRange, pilotId])
    const { data, isLoading } = useBatchPilotTrackingQuery(queries)
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)

    const currentFlights = data?.currentTimeRangeFlights?.[0]?.currentFlights ?? 0
    const prevFlights = data?.prevTimeRangeFlights?.[0]?.prevFlights ?? 0
    const currentHours = data?.currentTimeRangeHours?.[0]?.currentHours ?? 0
    const prevHours = data?.prevTimeRangeHours?.[0]?.prevHours ?? 0
    const currentDistance = data?.currentTimeRangeDistance?.[0]?.currentDistance ?? 0
    const prevDistance = data?.prevTimeRangeDistance?.[0]?.prevDistance ?? 0

    const topAircraft = data?.pilotTopAircraft?.[0]
    const pilotDroneHours = topAircraft?.pilotTopAircraftHours ?? 0
    const pilotAllTimeHours = data?.pilotAllTimeHours?.[0]?.pilotAllTimeHours ?? 0
    const droneSharePercentage = pilotAllTimeHours > 0 ? pilotDroneHours / pilotAllTimeHours : 0

    const totalFlightsPodium =
        (data?.currentTimeRangeFlightsPodium?.findIndex((v) => v["user_id"] === pilotId) ?? -1) + 1
    const totalHoursPodium =
        (data?.currentTimeRangeHoursPodium?.findIndex((v) => v["mission.user_id"] === pilotId) ??
            -1) + 1
    const distanceFlownPodium =
        (data?.currentTimeRangeDistanceFlownPodium?.findIndex(
            (v) => v["mission.user_id"] === pilotId
        ) ?? -1) + 1

    return (
        <div className="flex flex-col gap-4 pb-4">
            <h1 className="text-gray-50 telegraf text-[28px] pb-4">Activity</h1>
            <div className="h-[250px]">
                <PilotActivityGraph userId={user.id} />
            </div>
            <CardCollection
                cards={[
                    <MetricStat
                        key="flights"
                        title="Flights"
                        Icon={Icons.Drone}
                        value={currentFlights}
                        prevValue={prevFlights}
                        displayValue={currentFlights}
                        graphData={data?.currentTimeRangeFlightsGraph ?? []}
                        graphValueField="totalFlightsGraph"
                        podiumPlace={totalFlightsPodium}
                    />,
                    <MetricStat
                        key="hours"
                        title="Hours"
                        Icon={Icons.Controller}
                        value={currentHours}
                        prevValue={prevHours}
                        displayValue={currentHours.toFixed(1)}
                        graphData={data?.currentTimeRangeHoursGraph ?? []}
                        graphValueField="totalHoursGraph"
                        podiumPlace={totalHoursPodium}
                    />,
                    <MetricStat
                        key="distance"
                        title="Distance"
                        Icon={Icons.Route}
                        value={currentDistance}
                        prevValue={prevDistance}
                        displayValue={formatDistance({
                            value: currentDistance,
                            unit: distanceUnit,
                        })}
                        graphData={data?.currentTimeRangeDistanceFlownGraph ?? []}
                        graphValueField="distanceFlownGraph"
                        podiumPlace={distanceFlownPodium}
                    />,
                ]}
            />
            <div>{/* Spacer */}</div>
            <div className="flex gap-4">
                <div className="flex-[5] h-full">
                    <PlatformActivityCard userId={user.id!} />
                </div>
                <div className="flex-[6] h-full">
                    <MostUsedDroneCard
                        hours={topAircraft?.pilotTopAircraftHours}
                        percentage={droneSharePercentage}
                        id={topAircraft?.["mission.aircraft.id"]}
                        nickname={topAircraft?.["mission.aircraft.nickname"]}
                        make={topAircraft?.["mission.aircraft.manufacturer"]}
                        model={topAircraft?.["mission.aircraft.model"]}
                    />
                </div>
            </div>
            <div>{/* Spacer */}</div>
            <PilotCertificates
                pilotId={pilotId}
                certificates={data?.certificates ?? []}
                isLoading={isLoading}
            />
        </div>
    )
}

const MostUsedDroneCard = ({
    hours,
    percentage,
    id,
    nickname,
    make,
    model,
}: {
    hours: number | undefined | null
    percentage: number | undefined | null
    id: string | undefined | null
    nickname: string | undefined | null
    make: string | undefined | null
    model: string | undefined | null
}) => {
    const { displayName, model: cleanModel } = getDisplayableAircraftData({ nickname, make, model })
    const hasData = !!hours && !!percentage

    const { blobUrl, alt, imagePadding } = useAircraftImage({
        make,
        model,
        quality: "half",
    })

    // These calculations make it so every image is visually the same size regardless of any padding it may have.
    const targetContentWidthPct = 150
    const horizontalContentFraction = imagePadding
        ? 1 - imagePadding.left - imagePadding.right
        : undefined
    const imageWidthPct = horizontalContentFraction
        ? targetContentWidthPct / horizontalContentFraction
        : undefined
    const shiftLeftPct = imagePadding ? imagePadding.left * 100 : undefined
    const verticalCenterOffsetPct = imagePadding
        ? 50 + (imagePadding.top - imagePadding.bottom) * 50
        : undefined

    const router = useRouter("/workspace/:workspaceId")

    return (
        <Card className="w-full h-full !p-0 relative bg-white-100 overflow-hidden">
            {hasData && (
                <div className="flex h-full">
                    <div className="shrink-0 pl-10 py-9 flex flex-col justify-between relative z-10 w-[250px] overflow-visible">
                        <div className="flex flex-col gap-2.5">
                            <div className="text-gray-50 text-[22px] telegraf">Most used</div>
                            <small className="xs text-gray-100">{Math.round(hours)} hours</small>
                        </div>
                        <div className="w-[185px]">
                            <GaugeChart percentage={percentage} subtitle="Of total hours" />
                        </div>
                        <router.Link
                            to={{
                                path: "/fleet/aircraft/:aircraftId",
                                params: { aircraftId: id ?? "" },
                            }}
                        >
                            <div className="flex flex-col gap-1 group">
                                <div className="text-[32px] text-white telegraf w-fit max-w-[360px] truncate py-2 group-hover:text-blue-500 group-hover:underline">
                                    {displayName}
                                </div>
                                <div className="text-base text-gray-300 group-hover:text-blue-500 group-hover:underline">
                                    {cleanModel}
                                </div>
                            </div>
                        </router.Link>
                    </div>
                    <div className="relative flex-1">
                        {!!blobUrl &&
                            imageWidthPct !== undefined &&
                            shiftLeftPct !== undefined &&
                            verticalCenterOffsetPct !== undefined && (
                                <img
                                    src={blobUrl}
                                    alt={alt}
                                    className="absolute top-1/2 left-0 h-auto max-w-none"
                                    style={{
                                        width: `${imageWidthPct.toFixed(1)}%`,
                                        transform: `translateX(-${shiftLeftPct.toFixed(1)}%) translateY(-${verticalCenterOffsetPct.toFixed(1)}%)`,
                                        maskImage:
                                            "linear-gradient(to right, transparent 0%, black 35%)",
                                        WebkitMaskImage:
                                            "linear-gradient(to right, transparent 0%, black 35%)",
                                    }}
                                />
                            )}
                    </div>
                </div>
            )}
        </Card>
    )
}

const HistoryTab = ({ userId }: { userId: string }) => {
    const dispatch = useAppDispatch()
    const view = useAppSelector(selectHistoryView)
    const search = useAppSelector(selectFlightsSearch)
    useEffect(() => {
        dispatch(setHistoryView("table"))
    }, [dispatch])
    return view === "table" ? (
        <div className="flex flex-col gap-4 pb-6 h-full overflow-hidden">
            <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="p-px">
                        <InputOutlined
                            value={search}
                            onChange={(e) => dispatch(setSearch(e.currentTarget.value))}
                            placeholder="Search"
                            leftItem={<Icons.Icon Icon={Icons.MagnifyingGlass} className="mr-1" />}
                            size="sm"
                        />
                    </div>
                    <div className="h-full shrink-0">
                        <MissionsTableFilterBar filters={["date", "drone", "tag"]} />
                    </div>
                </div>
                <div className="grow" />
                <PilotHistoryTabSwitcher />
            </div>
            <div className="flex-1 shrink min-h-0 h-full">
                <MissionsTable
                    columns={[
                        "started_at",
                        "drone",
                        "flightTime",
                        "distanceFlown",
                        "maxAltitude",
                        "maxSpeed",
                    ]}
                    selectable={false}
                    editable={false}
                    filters={{ userId }}
                    origin={{ page: "pilot", pilotId: userId }}
                />
            </div>
        </div>
    ) : (
        <PilotMapView userId={userId} />
    )
}

const MetricStat = ({
    title,
    Icon,
    value,
    prevValue,
    displayValue,
    graphData,
    graphValueField,
    podiumPlace,
}: {
    title: string
    Icon: Icons.IconComponent
    value: number
    prevValue: number
    displayValue: string | number
    graphData: unknown[]
    graphValueField: string
    podiumPlace: number | undefined
}) => {
    const activityRange = useAppSelector(selectActivityTimeRange)
    const timeRangeText = useMemo(() => {
        switch (activityRange) {
            case "1y":
                return "1 year"
            case "30d":
                return "30 days"
            case "6m":
                return "6 months"
            case "7d":
                return "7 days"
            case "all":
                return "All time"
        }
    }, [activityRange])
    return (
        <div className="flex-1 flex flex-col gap-6 min-w-0 p-6">
            <div className="flex justify-between items-center min-h-[38px]">
                <Icons.SmallIcon Icon={Icon} className="text-white-200" />
                <PodiumBadge place={podiumPlace} />
            </div>
            <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                    <h5 className="text-gray-400 font-mono">{title}</h5>
                    <div className="text-white-300">{timeRangeText}</div>
                </div>
                <div className="flex items-end gap-1">
                    <h2 className="text-white telegraf">{displayValue}</h2>
                    {activityRange !== "all" && <Percent current={value} previous={prevValue} />}
                </div>
            </div>
            <ActivityCardGraph dateField="bucket" dbData={graphData} valueField={graphValueField} />
        </div>
    )
}

const Percent: FunctionComponent<{
    current: number
    previous: number
}> = ({ current, previous }) => {
    if (previous === 0) {
        if (current === 0) return <span>- 0%</span>
        return <span className="text-seafoam">↑ 100%</span>
    }

    const percent = ((current - previous) / previous) * 100
    const isPositive = percent > 0

    if (percent === 0) return null

    return (
        <span
            data-positive={isPositive}
            className={"flex items-center gap-1 text-seafoam data-[positive=false]:text-red-500"}
        >
            <Icons.SmallIcon Icon={isPositive ? Icons.ArrowCoolUp : Icons.ArrowCoolDown} />
            {Math.abs(percent).toFixed(1)}%
        </span>
    )
}

interface ActivityCardGraphProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dbData: any[]
    dateField: string
    valueField: string
}

const ActivityCardGraph: FunctionComponent<ActivityCardGraphProps> = ({
    dbData,
    dateField,
    valueField,
}) => {
    const chartData = useMemo(() => {
        if (!dbData || dbData.length === 0) return []

        const earliestDateStr = dbData.reduce((min, row) => {
            const currentDate = row[dateField]
            return currentDate < min ? currentDate : min
        }, dbData[0][dateField])

        const startDate = new Date(earliestDateStr)
        startDate.setHours(0, 0, 0, 0)

        const endDate = new Date()
        endDate.setHours(23, 59, 59, 999)

        return padTimeSeriesData(dbData, dateField, valueField, startDate, endDate, "day")
    }, [dbData, dateField, valueField])

    return (
        <div className="h-[16px] w-full min-w-0 overflow-hidden">
            <PilotTrackingCardChart data={chartData} />
        </div>
    )
}
